import { Box, Button, Center, Flex, Spacer, Stack, Text } from '@chakra-ui/react'
import { useRouter } from 'next/router'
import React, { useCallback, useState } from 'react'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC, { RPCRefresh, RPCReload } from '@/client/rpc'
import type { SerializedRichText } from '@/client/services/richText/types'
import {
  MODERATION_BOT_DISPLAY_NAME,
  type UserAdminMessageType,
} from '@/client/services/userAdminMessage'

import PublicImage from '@/components/PublicImage/PublicImage'
import ShwLink from '@/components/ShwLink'
import Form from '@/components/UI/Form/Form'
import { InputText } from '@/components/UI/Form/styled'
import type { FormInputType, FormValues } from '@/components/UI/Form/types'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import RichText from '@/components/UI/RichTextEditor/RichText'
import UserAvatar from '@/components/UserAvatar'

export default function UserAdminMessageModal({
  id,
  userId,
  userAvatarUrl,
  userDisplayName,
  content,
  acknowledgmentText,
  messageType,
  isDisputable,
}: {
  id: number
  userId: number | null
  userAvatarUrl: string | null
  userDisplayName: string | null
  content: SerializedRichText
  acknowledgmentText: string | null
  messageType: UserAdminMessageType
  isDisputable?: boolean
}): JSX.Element | null {
  const router = useRouter()

  const [open, setOpen] = useState(true)
  const [confirmationText, setConfirmationText] = useState('')
  const [isDisputing, setIsDisputing] = useState(false)

  const requiresAcknowledgmentText = acknowledgmentText != null && acknowledgmentText.length > 0

  const disableAcknowledgment =
    requiresAcknowledgmentText &&
    confirmationText.toLowerCase().trim() !== acknowledgmentText.toLowerCase().trim()

  const disputeDetectedIssueFormInputs: FormInputType[] = React.useMemo(
    () => [
      {
        label: 'Please explain why you think this flag is a mistake.',
        type: 'textarea',
        name: 'reason',
        required: true,
        size: 6,
      },
    ],
    []
  )

  const [handleAcknowledgeUserAdminMessage, isAcknowledging] = useAPIWrapper(
    async () => {
      // We want to reload the page to load any other admin messages
      await RPCReload.acknowledgeUserAdminMessage({
        id,
      })
      setOpen(false)
    },
    [id],
    'Acknowledge User Admin Message'
  )

  const [handleAcknowledgeAndSendMessageCallback, isAcknowledgingAndSending] = useAPIWrapper(
    async () => {
      // We want to reload the page to load any other admin messages, but we use RPCRefresh here because we reload when redirecting to the message conversation
      await RPCRefresh.acknowledgeUserAdminMessage({
        id,
      })
      setOpen(false)

      if (userId == null) {
        return
      }

      const conversation = await RPC.startDirectConversation({
        targetUserId: userId,
      })
      await router.push(`/messages#${conversation.external_uuid}`)
    },
    [id, userId, router],
    'Start Conversation'
  )

  const handleDisputeDetectedIssue = useCallback(
    async (values: FormValues) => {
      await RPCReload.disputeDetectedIssue({
        userAdminMessageId: id,
        disputeDetectedIssueText: `${values.reason}`,
      })
      setOpen(false)
    },
    [id]
  )

  return (
    <ModalV3
      open={open}
      size="xl"
      title={!isDisputing ? 'Message from the Schoolhouse Team' : 'Dispute this Flag'}
      onClose={() => setOpen(false)}
      disableClose
      disableClickOutside
      rightFooterContent={
        !isDisputing
          ? () => (
              <>
                <Button
                  onClick={handleAcknowledgeUserAdminMessage}
                  isLoading={isAcknowledging}
                  isDisabled={disableAcknowledgment}
                >
                  Acknowledge
                </Button>
                {userId != null ? (
                  <Button
                    onClick={handleAcknowledgeAndSendMessageCallback}
                    isLoading={isAcknowledgingAndSending}
                    isDisabled={disableAcknowledgment}
                  >
                    Acknowledge and Start DM with {userDisplayName}
                  </Button>
                ) : null}
              </>
            )
          : null
      }
      body={() =>
        !isDisputing ? (
          <Box>
            <Stack direction="row" align="center" spacing="12px">
              {userAvatarUrl != null && userDisplayName != null ? (
                <UserAvatar
                  src={userAvatarUrl}
                  name={userDisplayName}
                  size="md"
                  progressionLevel={null}
                />
              ) : (
                <Center width="48px" height="48px" bgColor="info.solid" borderRadius="24px">
                  <PublicImage
                    src="/logo-square-v3.png"
                    maxW="100%"
                    maxH="100%"
                    borderRadius="24px"
                  />
                </Center>
              )}
              <Text>
                From <b>{userAvatarUrl != null ? userDisplayName : MODERATION_BOT_DISPLAY_NAME}</b>
              </Text>
            </Stack>
            <Box bg="bg.muted" borderRadius="8px" p="16px" mt="16px">
              <RichText content={content} />
            </Box>
            {requiresAcknowledgmentText ? (
              <Stack direction="column" spacing="8px" mt="32px">
                <Text>
                  Please type <b>{acknowledgmentText}</b> to confirm that you have read this
                  message.
                </Text>
                <InputText
                  placeholder={acknowledgmentText}
                  value={confirmationText}
                  onChange={e => setConfirmationText(e.target.value)}
                />
              </Stack>
            ) : null}
            {messageType === 'detected-issue-warning' && isDisputable != null && isDisputable ? (
              <Text mt="4px" variant="supporting">
                After reviewing the policy, do you think this flag might be a mistake? Please let us
                know{' '}
                <ShwLink onClick={() => setIsDisputing(true)} color="primary.fg">
                  here
                </ShwLink>
                .
              </Text>
            ) : null}
          </Box>
        ) : (
          <Box>
            <Form
              name="DisputeDetectedIssue"
              blocks={disputeDetectedIssueFormInputs}
              onSubmit={handleDisputeDetectedIssue}
              FooterComponent={({ submitting }) => (
                <Flex mt="16px">
                  <Spacer />
                  <Button
                    variant="outline"
                    isDisabled={submitting}
                    onClick={() => {
                      setConfirmationText('')
                      setIsDisputing(false)
                    }}
                    mr="8px"
                  >
                    Back
                  </Button>
                  <Button isLoading={submitting} type="submit">
                    Submit
                  </Button>
                </Flex>
              )}
            />
          </Box>
        )
      }
    />
  )
}
